import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import DashboardLayout from "../../common/DashboardLayout";
import CertificationServices from "../../../services/CertificationServices";
import TeacherServices from "../../../services/TeacherServices";

// Helper to normalize teacher records from API response
const normalizeTeacherRecords = (payload) => {
  const list = (() => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.certifications)) return payload.certifications;
    if (Array.isArray(payload?.teachers)) return payload.teachers;
    if (Array.isArray(payload?.records)) return payload.records;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  })();

  return list
    .map((entry) => {
      // FIX: Handle flat teacher object (from getAllTeachers) by traversing or using entry itself
      const teacher =
        entry.teacher || entry.teacherInfo || entry.user || entry.profile || entry || {};

      const teacherId =
        teacher.id ||
        teacher._id ||
        entry.teacherId ||
        entry.userId ||
        entry.id ||
        "";

      if (!teacherId) return null;

      const roles = teacher.roles || [];
      const isSchoolAdmin = roles.includes("1") || roles.includes(1);

      return {
        teacherId: String(teacherId),
        teacherName:
          teacher.name ||
          teacher.fullName ||
          `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() ||
          entry.teacherName ||
          "Unknown User",
        teacherEmail: teacher.email || entry.teacherEmail || "",
        firstName: teacher.firstName || "",
        lastName: teacher.lastName || "",
        roles: roles,
        isSchoolAdmin: isSchoolAdmin,
      };
    })
    .filter(Boolean);
};

const AdministratorManagement = () => {
  const [allUsers, setAllUsers] = useState([]); // Store ALL users for autocomplete
  const [adminRecords, setAdminRecords] = useState([]); // Filtered list for display
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");

  // Form State
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: ""
  });
  const [suggestions, setSuggestions] = useState([]);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [existingUserId, setExistingUserId] = useState(null);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = String(currentUser._id || currentUser.id || '');

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await TeacherServices.getAllTeachers();
      console.log("🔍 [AdminManagement] getAllTeachers response:", response);
      // getAllTeachers returns { teachers: [...] }
      const normalized = normalizeTeacherRecords(response);
      console.log("🔍 [AdminManagement] Normalized users:", normalized.length, normalized);
      setAllUsers(normalized);
      // Filter where Record IS a school admin
      setAdminRecords(normalized.filter(r => r.isSchoolAdmin));

      // Reset form on reload
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      console.error("Failed to load users", err);
      setError("We couldn't load administrator records. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const resetForm = () => {
    setFormData({ firstName: "", lastName: "", email: "" });
    setSuggestions([]);
    setIsExistingUser(false);
    setExistingUserId(null);
  }

  // Handle Email Input & Autocomplete
  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData(prev => ({ ...prev, email }));

    console.log("🔍 [handleEmailChange] Email input:", email);
    console.log("🔍 [handleEmailChange] allUsers count:", allUsers.length);
    console.log("🔍 [handleEmailChange] Sample user:", allUsers[0]);

    if (!email || email.length < 2) {
      setSuggestions([]);
      return;
    }

    const matches = allUsers.filter(u => {
      const userEmail = u.teacherEmail || u.email || "";
      const match = userEmail.toLowerCase().includes(email.toLowerCase());
      if (match) {
        console.log("🔍 [handleEmailChange] Matched user:", u);
      }
      return match;
    });
    console.log("🔍 [handleEmailChange] Matches found:", matches.length, matches);
    setSuggestions(matches.slice(0, 5)); // Limit to 5 suggestions

    // Check for exact match to prefill/lock
    const exactMatch = allUsers.find(u => {
      const userEmail = u.teacherEmail || u.email || "";
      return userEmail.toLowerCase() === email.toLowerCase();
    });
    if (exactMatch) {
      setIsExistingUser(true);
      setExistingUserId(exactMatch.teacherId);
      // Auto-fill names if they exist, otherwise keep what the user typed? 
      // Better to use existing user's name.
      // Split full name if first/last missing
      let fName = exactMatch.firstName;
      let lName = exactMatch.lastName;
      if (!fName && !lName) {
        const parts = exactMatch.teacherName.split(' ');
        fName = parts[0];
        lName = parts.slice(1).join(' ');
      }
      setFormData(prev => ({
        ...prev,
        firstName: fName,
        lastName: lName
      }));
      setSuggestions([]); // Hide suggestions on exact match
    } else {
      setIsExistingUser(false);
      setExistingUserId(null);
    }
  };

  const handleSuggestionClick = (user) => {
    let fName = user.firstName;
    let lName = user.lastName;
    if (!fName && !lName) {
      const parts = user.teacherName.split(' ');
      fName = parts[0];
      lName = parts.slice(1).join(' ');
    }

    setFormData({
      email: user.teacherEmail,
      firstName: fName,
      lastName: lName
    });
    setSuggestions([]);
    setIsExistingUser(true);
    setExistingUserId(user.teacherId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) {
      Swal.fire("Required", "Please fill in all fields.", "warning");
      return;
    }

    try {
      setIsLoading(true);
      if (isExistingUser && existingUserId) {
        // PROMOTE EXISTING USER
        await TeacherServices.updateTeacher({
          id: existingUserId,
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          isSchoolAdmin: true
        });
        await Swal.fire("Promoted!", `${formData.firstName} is now an Administrator.`, "success");
      } else {
        // CREATE NEW USER (as Admin)
        const fullName = `${formData.firstName} ${formData.lastName}`.trim();
        await TeacherServices.addSchoolTeacher(fullName, formData.email, true);
        await Swal.fire("Created!", `${fullName} has been added as a School Admin. Credentials sent.`, "success");
      }
      loadRecords();
    } catch (err) {
      console.error("Save failed", err);
      Swal.fire("Error", err.response?.data?.error || "Failed to save administrator.", "error");
      setIsLoading(false); // Only set false on error, success reloads which handles loading state
    }
  };

  const filteredDisplayRecords = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return adminRecords.filter((record) => {
      return (
        !term ||
        record.teacherName.toLowerCase().includes(term) ||
        record.teacherEmail.toLowerCase().includes(term)
      );
    });
  }, [adminRecords, searchTerm]);

  const userobj = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <DashboardLayout
      role={1}
      userType={userobj?.type}
      activeMenu="Administrator Management"
      layoutType="topnav"
    >
      <div className="card">
        <div className="card-body">
          <div className="d-flex flex-column flex-lg-row gap-3 justify-content-between align-items-lg-center mb-4">
            <div>
              <h4 className="mb-1">Administrator Management</h4>
              <p className="text-muted mb-0">
                Manage school administrators who have full access to school settings.
              </p>
            </div>
            <div className="d-flex gap-2">
              <input
                type="search"
                className="form-control"
                placeholder="Search admins"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                style={{ maxWidth: '300px' }}
              />
              <button
                className={`btn ${showAddForm ? 'btn-outline-danger' : 'btn-primary'}`}
                onClick={() => {
                  setShowAddForm(!showAddForm);
                  resetForm(); // Reset on toggle
                }}
              >
                {showAddForm ? <i className="bx bx-x"></i> : <i className="bx bx-plus"></i>}
                {showAddForm ? " Cancel" : " Add Administrator"}
              </button>
            </div>
          </div>

          {/* INLINE ADD FORM */}
          {showAddForm && (
            <div className="card border mb-4 bg-light">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    {isExistingUser ? "Promote Existing User to Admin" : "Add New Administrator"}
                  </h5>
                  <span className="badge bg-info">
                    Debug: {allUsers.length} users loaded | {suggestions.length} suggestions
                  </span>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Email Address</label>
                      <div className="position-relative">
                        <input
                          type="email"
                          className="form-control"
                          placeholder="Enter email to search..."
                          value={formData.email}
                          onChange={handleEmailChange}
                          autoComplete="off"
                        />
                        {/* Autocomplete Suggestions */}
                        {suggestions.length > 0 && (
                          <div className="list-group position-absolute w-100 shadow" style={{ zIndex: 1000, top: '100%' }}>
                            {suggestions.map(s => (
                              <button
                                key={s.teacherId}
                                type="button"
                                className="list-group-item list-group-item-action"
                                onClick={() => handleSuggestionClick(s)}
                              >
                                <div><strong>{s.teacherName}</strong></div>
                                <small className="text-muted">{s.teacherEmail}</small>
                                {s.isSchoolAdmin && <span className="badge bg-warning ms-2">Already Admin</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {isExistingUser && (
                        <small className="text-success fw-bold">
                          <i className="bx bx-check-circle"></i> Existing user found. Will promote to admin.
                        </small>
                      )}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.firstName}
                        onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                        disabled={isExistingUser} // Disable if existing user to prevent accidental rename? Or allow edit?
                      // Requirement said "prefill", usually implies editable or locked. 
                      // I'll leave editable but maybe useful to lock to avoid confusion. 
                      // Let's keep editable for flexibility but default from logic.
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.lastName}
                        onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                        disabled={isExistingUser}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={isLoading || (isExistingUser && adminRecords.some(r => r.teacherId === existingUserId))}
                    >
                      {isLoading ? "Saving..." : isExistingUser ? "Promote to Admin" : "Create Administrator"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {error && <div className="alert alert-warning mb-4">{error}</div>}

          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && !showAddForm ? ( // Only show loader if not submitting form (which has its own loader logic)
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredDisplayRecords.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center text-muted py-4">
                      No administrators found.
                    </td>
                  </tr>
                ) : (
                  filteredDisplayRecords.map((record) => {
                    const isSelf = String(record.teacherId) === currentUserId;
                    return (
                      <tr key={record.teacherId}>
                        <td>
                          <span className="fw-semibold">{record.teacherName}</span>
                          {isSelf && <span className="badge bg-soft-info text-info ms-2">You</span>}
                        </td>
                        <td>
                          <a href={`mailto:${record.teacherEmail}`} className="text-decoration-none">
                            {record.teacherEmail}
                          </a>
                        </td>
                        <td>
                          <span className="badge bg-soft-success text-success">Active</span>
                        </td>
                        <td className="text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <Link
                              to={`/editTeacher?teacher_id=${record.teacherId}`}
                              className="btn btn-outline-secondary btn-sm"
                            >
                              Edit
                            </Link>
                            {!isSelf && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={async () => {
                                  const result = await Swal.fire({
                                    title: 'Remove Admin Access?',
                                    text: `This will remove ${record.teacherName}'s admin check. To completely delete the user, delete them from the Teachers list.`,
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonText: 'Demote to Teacher',
                                    cancelButtonText: 'Cancel'
                                  });

                                  if (result.isConfirmed) {
                                    try {
                                      setIsLoading(true);
                                      await TeacherServices.updateTeacher({
                                        id: record.teacherId,
                                        name: record.teacherName,
                                        email: record.teacherEmail,
                                        isSchoolAdmin: false // DEMOTE
                                      });
                                      await Swal.fire('Demoted', 'User is no longer an admin.', 'success');
                                      loadRecords();
                                    } catch (e) {
                                      console.error(e);
                                      Swal.fire('Error', 'Failed to demote user', 'error');
                                      setIsLoading(false);
                                    }
                                  }
                                }}
                              >
                                Remove Admin
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdministratorManagement;
